import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { springPresets } from "@/lib/motion";

interface PlanSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

export function PlanSection({ title, children, className }: PlanSectionProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      className={cn("mb-12", className)}
    >
      <h2 className="text-3xl font-bold mb-6 text-foreground">{title}</h2>
      <Card className="p-8 bg-card border-border shadow-lg">
        {children}
      </Card>
    </motion.section>
  );
}

interface RequirementTableProps {
  data: Array<{
    category: string;
    requirement: string;
    priority: string;
  }>;
}

export function RequirementTable({ data }: RequirementTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted">
            <th className="border border-border px-4 py-3 text-left font-semibold text-foreground">분류</th>
            <th className="border border-border px-4 py-3 text-left font-semibold text-foreground">요구사항</th>
            <th className="border border-border px-4 py-3 text-left font-semibold text-foreground">우선순위</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <motion.tr
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.snappy, delay: index * 0.05 }}
              className="hover:bg-accent/50 transition-colors"
            >
              <td className="border border-border px-4 py-3 text-foreground">{row.category}</td>
              <td className="border border-border px-4 py-3 text-foreground">{row.requirement}</td>
              <td className="border border-border px-4 py-3">
                <span
                  className={cn(
                    "inline-block px-3 py-1 rounded-full text-sm font-medium",
                    row.priority === "높음" && "bg-destructive/20 text-destructive",
                    row.priority === "중간" && "bg-primary/20 text-primary",
                    row.priority === "낮음" && "bg-muted text-muted-foreground"
                  )}
                >
                  {row.priority}
                </span>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ScheduleChartProps {
  data: Array<{
    phase: string;
    startWeek: number;
    endWeek: number;
    tasks: string[];
  }>;
}

export function ScheduleChart({ data }: ScheduleChartProps) {
  const totalWeeks = Math.max(...data.map(item => item.endWeek));
  const colors = [
    "bg-chart-1",
    "bg-chart-2",
    "bg-chart-3",
    "bg-chart-4",
    "bg-chart-5",
  ];

  return (
    <div className="space-y-6">
      {data.map((phase, index) => {
        const duration = phase.endWeek - phase.startWeek + 1;
        const startPercent = ((phase.startWeek - 1) / totalWeeks) * 100;
        const widthPercent = (duration / totalWeeks) * 100;

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: index * 0.1 }}
            className="space-y-2"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">{phase.phase}</h3>
              <span className="text-sm text-muted-foreground">
                {phase.startWeek}주차 - {phase.endWeek}주차
              </span>
            </div>
            <div className="relative h-12 bg-muted rounded-lg overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${widthPercent}%` }}
                transition={{ ...springPresets.smooth, delay: index * 0.1 + 0.2 }}
                className={cn(
                  "absolute h-full rounded-lg",
                  colors[index % colors.length],
                  "opacity-80"
                )}
                style={{ left: `${startPercent}%` }}
              />
            </div>
            <ul className="ml-4 space-y-1">
              {phase.tasks.map((task, taskIndex) => (
                <li key={taskIndex} className="text-sm text-muted-foreground flex items-start">
                  <span className="mr-2">•</span>
                  <span>{task}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        );
      })}
      <div className="flex justify-between mt-4 pt-4 border-t border-border">
        {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((week) => (
          <div key={week} className="text-xs text-muted-foreground text-center" style={{ width: `${100 / totalWeeks}%` }}>
            {week}주
          </div>
        ))}
      </div>
    </div>
  );
}